/** @jsxImportSource ./simboluri-jsx */
import { textVerdict } from "./elemente";
import { PAS_FUNDATIE_PREDIM } from "./fundatia";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { ArmareStalpiResult, FemModel, NivelStalp, SistemDirectie, StalpArmat, StepState, fmtDate } from "./tipuri";
import Info from "./Info";
import GammaRdTabel, { zecGamma } from "./GammaRdTabel";
import TitluVerificare, { useCatalogVerificari } from "./Verificare";
import { Pas } from "./Explica";
import LocalizareCladire from "./LocalizareCladire";
import { incadreaza } from "./incadrare3d";
import { Focus, refFaraNivel } from "./legaturi";
import { DescriereaPasului } from "./TitluPas";
import VedereStalpCotata, { FundatieStalp, fundatiaStalpului } from "./DesenStalpCotat";
import { CotaH, CotaV, cmFmt } from "./cote";
import { t, tn, tx, useT } from "../i18n";

// Pasul 15 — Armarea stâlpilor + verificarea nodurilor grindă–stâlp:
// capacitatea vine din motorul de fibre (înlocuitorul XTRACT), verificarea
// = fiecare pereche (N, M2, M3) în interiorul suprafeței P–M–M („ceapa"),
// plus νd, ierarhia nodului, tăietoarea capabilă și detaliile de nod.

function fmt(v: number | null | undefined, dec = 1): string {
  if (v === null || v === undefined) return "—";
  return v.toFixed(dec);
}

function Verdict({ v }: { v: string }) {
  const cls = v.startsWith("SE VERIFICA") ? "arm-ok"
    : v.startsWith("ATENTIE") ? "arm-warn"
      : v.startsWith("NU") ? "arm-bad" : "arm-neutral";
  return <span className={`arm-verdict ${cls}`}>{textVerdict(v)}</span>;
}

export default function StepArmareStalpi({
  state,
  femState,
  fundatieState,
  running,
  focus,
  projectId,
  solutie,
}: {
  state: StepState | null;
  // modelul Pasului 7, doar pentru vizualizarea «unde e stâlpul în clădire»
  femState?: StepState | null;
  // Pasul 6: adâncimea de fundare + secțiunea tălpii, pentru vederea cotată
  // a stâlpului întreg
  fundatieState?: StepState | null;
  running: boolean;
  // stâlpul cerut dintr-un link (?focus=ST11.3) — v. legaturi.ts
  focus?: Focus;
  // proiectul + soluția vizualizată: viewerul «unde e în clădire» le folosește
  // ca să poată sări la grinda sau la peretele pe care dai click
  projectId?: string;
  solutie?: string | null;
}) {
  useT();
  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.ars.pasul_ruleaza")}</strong>
          <div className="sg-muted">
            {t("struct.ars.stalp_genereaza_suprafet")}
          </div>
        </div>
      </div>
    );
  }
  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.ars.pasul_nu_rulat")}
        {/* descrierea vine din catalog_pasi.py — același text cu
            cel de la hover pe titlul pasului */}
        <DescriereaPasului cheie="armare_stalpi" />
      </div>
    );
  }
  const r = state.result as unknown as ArmareStalpiResult;
  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">
          {t("struct.ars.ultima_rulare_esuat", {
            motiv: r?.error ?? t("struct.ars.eroare_necunoscuta"),
          })}
        </div>
        <div className="sg-muted">{t("struct.ars.ruleaza_nou_meniul")}</div>
      </div>
    );
  }
  return <StalpiView r={r} model={modelFem(femState)} updatedAt={state.updated_at}
                     fundatieState={fundatieState ?? null}
                     focus={focus} projectId={projectId} solutie={solutie} />;
}

// modelul FEM din starea Pasului 7, dacă a rulat
function modelFem(st: StepState | null | undefined): FemModel | null {
  return ((st?.result as { model?: FemModel } | null)?.model) ?? null;
}

function StalpiView({ r, model, updatedAt, fundatieState, focus, projectId, solutie }: {
  r: ArmareStalpiResult;
  model: FemModel | null;
  updatedAt: string;
  fundatieState: StepState | null;
  focus?: Focus;
  projectId?: string;
  solutie?: string | null;
}) {
  useT();
  const [sel, setSel] = useState("");
  // un stâlp e o STIVĂ (ST11) cu câte un rând pe nivel (ST11.3): linkul
  // poartă rândul, selectorul cere stiva, iar tabul de nivel se alege din
  // sufix — v. `StalpDetaliu`
  useEffect(() => {
    if (focus?.ref) setSel(refFaraNivel(focus.ref));
  }, [focus?.n, focus?.ref]);
  const stalpi = useMemo(
    () => [...r.stalpi].sort((a, b) =>
      a.element.localeCompare(b.element, undefined, { numeric: true })),
    [r]
  );
  const s = stalpi.find((x) => x.element === sel) ?? stalpi[0];
  const p = r.parametri;
  return (
    <div className="st-result">
      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.ars.stalpi_lamele_armate")}
            <Info
              titlu={t("struct.ars.verifica_stalp")}
              ce={t("struct.ars.capacitatea_unei_sectiun")}
              deCe={t("struct.ars.pozitia_zonei_comprimate")}
              parametri={[
                { simbol: t("struct.ars.simbol_beton"), nume: String(p.beton), sursa: String(p.sursa_beton) },
                { simbol: t("struct.ars.simbol_gamma_nod"),
                  nume: t("struct.ars.gamma_nod_nume", {
                    v: zecGamma(p.gamma_rd_nod), sursa: p.gamma_rd_nod_sursa,
                  }),
                  sursa: t("struct.ars.clasa_ductilitate_pas3") },
              ]}
            />
          </div>
          <div className="st-card-value">{r.nr_stackuri}</div>
          <div className="st-card-sub">
            {t("struct.ars.stalpi_pe_niveluri", {
              n: r.nr_stalpi,
              verdicte: Object.entries(r.verdicte)
                .map(([k, v]) => t("struct.ars.verdict_x", { n: v, v: k }))
                .join(" · "),
            })}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">{t("struct.ars.parametrii_de_calcul")}</div>
          <div className="st-card-sub" style={{ marginTop: 6 }}>
            {t("struct.ars.param_beton", {
              beton: p.beton, fcd: p.f_cd_mpa, fyd: p.f_yd_mpa,
              clasa: p.clasa_ductilitate, nud: p.nu_d_limita,
              pmin: Number(p.p_min) * 100, pmax: Number(p.p_max) * 100,
            })}
          </div>
          <div className="st-card-sub" style={{ marginTop: 6 }}>
            {t("struct.ars.param_gamma", {
              nod: zecGamma(p.gamma_rd_nod),
              forf: zecGamma(p.gamma_rd_nod_forfecare),
              taie: zecGamma(p.gamma_rd_taietoare),
              anc: zecGamma(p.gamma_rd_ancorare),
              clasa: p.clasa_ductilitate,
            })}
          </div>
        </div>
      </div>

      <GammaRdTabel randuri={r.gamma_rd_tabel} clasa={p.clasa_ductilitate} />

      <SistemPeDirectii s={r.sistem_directii} />

      {r.avertismente.length > 0 && (
        <div className="st-findings">
          <h3>{t("struct.ars.avertismente")}</h3>
          {r.avertismente.map((a, i) => (
            <div key={i} className="st-warn">• {a}</div>
          ))}
        </div>
      )}

      <div className="st-findings">
        <label className="sg-muted">
          {t("struct.ars.stalpul_eticheta")}{" "}
          <select value={s?.element ?? ""} onChange={(e) => setSel(e.target.value)}>
            {stalpi.map((x) => (
              <option key={x.element} value={x.element}>
                {t("struct.ars.optiune_stalp", { el: x.element, verdict: x.verdict })}
              </option>
            ))}
          </select>
        </label>{" "}
        {s && <Verdict v={s.verdict} />}
      </div>

      {s && <StalpDetaliu s={s} model={model} cCm={Number(p.c_stalp_cm ?? 4) || 4}
                          fundatieState={fundatieState}
                          focus={focus} projectId={projectId} solutie={solutie}
                          // click pe ALT stâlp în viewer = se schimbă stiva
                          // selectată aici, fără reîncărcarea paginii
                          peStalp={(ref) => setSel(refFaraNivel(ref))} />}
      <div className="sg-muted st-ran-at">{t("struct.pas.rulat_la", { cand: fmtDate(updatedAt) })}</div>
    </div>
  );
}

/** Nota de calcul a unui stâlp, pe nivelul ales. `faraVederi` lasă deoparte
 *  tabul de niveluri, vederea cotată, localizarea și carcasa 3D: pagina
 *  elementului e deja a unui nivel și le are deasupra. */
export function StalpDetaliu({ s, model, cCm, fundatieState, focus, projectId, solutie,
                               peStalp, faraVederi }: {
  s: StalpArmat;
  model: FemModel | null;
  cCm: number;
  fundatieState?: StepState | null;
  focus?: Focus;
  projectId?: string;
  solutie?: string | null;
  peStalp?: (ref: string) => void;
  faraVederi?: boolean;
}) {
  useT();
  const [nivelIdx, setNivelIdx] = useState(0);
  // CE SE DESENEAZĂ: numai nivelul selectat (implicit — omul se uită la
  // armarea unui nivel) sau toată stiva, cross-etaje, cu fundația dedesubt.
    const [mod, setMod] = useState<"nivel" | "stalp">("nivel");
  // tabul de nivel al linkului: rândul cerut („ST11.3") e chiar `id_plan`
  useEffect(() => {
    if (!focus?.ref) return;
    const i = s.nivele.findIndex((x) => x.id_plan === focus.ref);
    if (i >= 0) setNivelIdx(i);
  }, [focus?.n, focus?.ref, s]);
  const n = s.nivele[Math.min(nivelIdx, s.nivele.length - 1)];
  // titlurile verificărilor vin din ACELAȘI catalog ca la Pasul 19
  const catalog = useCatalogVerificari();
  const totStalpul = mod === "stalp";
  // nivelurile DESENATE: unul, sau toată stiva
  const desenate = useMemo(
    () => (totStalpul ? s.nivele : [n]),
    [totStalpul, s.nivele, n]
  );
  // fundația de sub stâlpul ăsta — lățimea axului pe care stă și adâncimea
  // reală de fundare, nu o cutie fixă. `fundatieState` e deja geometria
  // ADOPTATĂ, aleasă de router (v. `fundatia.ts`); aici e împachetată la
  // loc sub cheia pe care o citește `fundatiaStalpului`.
  const fundatie = useMemo<FundatieStalp | null>(
    () =>
      fundatieState
        ? fundatiaStalpului({ [PAS_FUNDATIE_PREDIM]: fundatieState }, s.element)
        : null,
    [fundatieState, s.element]
  );
  return (
    <>
      <div className="st-findings" style={{ padding: 0, overflow: "hidden" }}>
        {!faraVederi && (<>
        <div style={{ display: "flex", gap: 8, padding: "8px 12px",
                      alignItems: "center", flexWrap: "wrap" }}>
          {s.nivele.map((x, i) => (
            <button key={i}
                    className={`md-tab ${!totStalpul && i === nivelIdx ? "active" : ""}`}
                    onClick={() => { setMod("nivel"); setNivelIdx(i); }}>
              {x.id_plan} ({x.nivel}) — {x.armare.n}Ø{x.armare.phi}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          {/* CE SE DESENEAZĂ — comutatorul: implicit doar
              nivelul selectat, la cerere tot stâlpul, cross-etaje */}
          <div className="pf-tabs" style={{ marginBottom: 0 }}>
            <button className={`pf-tab ${!totStalpul ? "active" : ""}`}
                    onClick={() => setMod("nivel")}
                    title={t("struct.ars.desenele_arata_numai")}>
              {t("struct.ars.doar_nivelul")}
            </button>
            <button className={`pf-tab ${totStalpul ? "active" : ""}`}
                    onClick={() => setMod("stalp")}
                    title={t("struct.ars.stiva_intreaga_talpa")}>
              {t("struct.ars.tot_stalpul")}
            </button>
          </div>
        </div>

        {/* VEDEREA COTATĂ — desenul de planșă: placa, banda grinzii,
            înălțimea liberă, zonele critice, înnădirea, fundația reală */}
        <div style={{ display: "flex", alignItems: "flex-start", flexWrap: "wrap",
                      borderTop: "1px solid #dedede" }}>
          <div style={{ flex: "1 1 420px", minWidth: 0, padding: "10px 12px" }}>
            <div className="ob-lbl" style={{ paddingBottom: 6 }}>
              {t("struct.ars.vederea_cotata", {
                ce: totStalpul
                  ? t("struct.ars.p1_nivelurile", { p1: s.element })
                  : n.id_plan,
              })}
            </div>
            <VedereStalpCotata
              toate={s.nivele}
              selectate={desenate}
              fundatie={fundatie}
              cuFundatie={totStalpul}
              cCm={cCm}
            />
          </div>
          <div style={{ flex: "0 0 340px", maxWidth: "100%",
                        borderLeft: "1px solid #dedede" }}>
            <LocalizareCladire
              model={model}
              evidentiate={desenate.map((x) => x.id_plan)}
              height={420}
              projectId={projectId}
              solutie={solutie}
              pasCurent="armare_stalpi"
              peElement={peStalp}
            />
          </div>
        </div>

        {/* carcasa 3D, restrânsă la ce e selectat */}
        <div style={{ borderTop: "1px solid #dedede" }}>
          <Stalp3D s={s} cCm={cCm} nivele={desenate} fundatie={fundatie} />
        </div>
        <div className="sg-muted" style={{ padding: "6px 12px", fontSize: 12 }}>
          {t("struct.ars.carcasa_de_armare", {
            ce: totStalpul
              ? t("struct.ars.toata_verticala_stack", { p1: s.element })
              : t("struct.ars.a_nivelului", { id: n.id_plan }),
          })}{" "}
          {t("struct.ars.carcasa_legenda", {
            fundatie: totStalpul && fundatie
              ? t("struct.ars.fundatia_desenata_adanci", {
                  p1: fundatie.Df_m.toFixed(2), p2: fundatie.B_m.toFixed(2),
                })
              : "",
          })}
        </div>
        </>)}
        <Ceapa3D n={n} />
        <div className="sg-muted" style={{ padding: "6px 12px", fontSize: 12 }}>
          {t("struct.ars.ceapa_legenda", {
            b: n.b_cm, h: n.h_cm, n: n.armare.n, phi: n.armare.phi,
          })}
        </div>
      </div>

      <div className="st-findings">
        <h3>{t("struct.ars.armarea_aleasa_sectiune", { id: n.id_plan })}</h3>
        <div className="sg-muted" style={{ marginBottom: 8 }}>
          {t("struct.ars.armare_simetrica_contur")}
        </div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
          <SectiuneStalp n={n} cCm={cCm} />
          <div style={{ minWidth: 260 }}>
            <table className="sg-table">
              <tbody>
                <tr><td>{t("struct.ars.longitudinal")}</td><td className="sg-mono">
                  {t("struct.ars.val_longitudinal", {
                    n: n.armare.n, phi: n.armare.phi,
                    p: (n.armare.p_efectiv * 100).toFixed(2),
                  })}
                </td></tr>
                <tr><td>{t("struct.ars.etrieri_zonele_plastice")}</td><td className="sg-mono">Ø{n.armare.etrieri.phi}/{n.armare.etrieri.pas_critic_cm} cm</td></tr>
                <tr><td>{t("struct.ars.etrieri_zona_curenta")}</td><td className="sg-mono">Ø{n.armare.etrieri.phi}/{n.armare.etrieri.pas_curent_cm} cm</td></tr>
                <tr><td>{t("struct.ars.zona_plastica_l")}</td><td className="sg-mono">{n.armare.l_cr_jos_m ?? n.armare.l_cr_m} / {n.armare.l_cr_sus_m ?? n.armare.l_cr_m} m</td></tr>
                {n.petrecere && (
                  <tr><td>{t("struct.ars.petrecerea_barelor")}</td><td className="sg-mono">
                    {t("struct.ars.val_petrecere", {
                      l0: n.petrecere.l0_m, z: n.petrecere.z_m,
                      etrieri: n.armare.etrieri.pas_innadire_cm
                        ? t("struct.ars.val_petrecere_etrieri", {
                            phi: n.armare.etrieri.phi,
                            pas: n.armare.etrieri.pas_innadire_cm,
                          })
                        : "",
                      ks: n.petrecere.in_zona_critica
                        ? t("struct.ars.val_petrecere_ks") : "",
                    })}
                  </td></tr>
                )}
                {n.petrecere === null && (
                  <tr><td>{t("struct.ars.petrecerea_barelor")}</td><td className="sg-mono">
                    {t("struct.ars.bara_trece_prin")}
                  </td></tr>
                )}
                <tr><td>{t("struct.ars.acoperirea")}</td><td className="sg-mono">{cCm} cm</td></tr>
              </tbody>
            </table>
          </div>
          {/* COTELE nivelului: cele trei înălțimi nu sunt aceeași mărime, iar
              */}
          <div style={{ minWidth: 300 }}>
            <table className="sg-table">
              <tbody>
                <tr>
                  <td>{t("struct.ars.inaltimea_de_nivel")} <span className="sg-muted">{t("struct.ars.ax_planseu_ax")}</span></td>
                  <td className="sg-mono">{n.H_m.toFixed(2)} m</td>
                </tr>
                <tr>
                  <td>{t("struct.ars.inaltimea_a_stalpului")} <strong>{t("struct.ars.libera")}</strong> {t("struct.ars.a_stalpului")}</td>
                  <td className="sg-mono" style={{ color: "var(--acc)" }}>
                    {n.h_liber_m != null ? `${n.h_liber_m.toFixed(2)} m` : "—"}
                  </td>
                </tr>
                <tr>
                  <td>{t("struct.ars.banda_planseului")} <span className="sg-muted">{t("struct.ars.grinda_cea_inalta")}</span></td>
                  <td className="sg-mono">
                    {n.h_grinda_nod_m != null ? `${n.h_grinda_nod_m.toFixed(2)} m` : "—"}
                  </td>
                </tr>
                <tr>
                  <td>{t("struct.ars.placa")}</td>
                  <td className="sg-mono">
                    {n.h_placa_m != null ? `${n.h_placa_m.toFixed(2)} m` : "—"}
                  </td>
                </tr>
                <tr>
                  <td>{t("struct.ars.innadirea_barelor_l0")}</td>
                  <td className="sg-mono" style={{ color: "var(--bad)" }}>
                    {n.l_0_m != null ? `${n.l_0_m.toFixed(2)} m` : "—"}
                  </td>
                </tr>
                <tr>
                  <td>{t("struct.ars.grosimea_sectiunii")} <span className="sg-muted">(b × h)</span></td>
                  <td className="sg-mono">
                    {t("struct.ars.val_bh_cm", { b: n.b_cm, h: n.h_cm })}
                  </td>
                </tr>
                <tr>
                  <td>
                    {t("struct.ars.inaltimea_flexibila")}
                    <Info
                      titlu={t("struct.ars.h_flex_coteaza")}
                      ce={t("struct.ars.h_flex_h")}
                      deCe={t("struct.ars.ofsetul_rigid_stalpului")}
                    />
                  </td>
                  <td className="sg-mono sg-muted">
                    {n.h_flex_m != null ? `${n.h_flex_m.toFixed(2)} m` : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
            {n.l_0_nota && (
              <div className="sg-muted" style={{ fontSize: 12, marginTop: 6 }}>
                {t("struct.ars.nota_l0", { nota: n.l_0_nota })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="st-findings">
        <h3>{t("struct.ars.verificarile_nivelului", { id: n.id_plan })}</h3>
        <div className="sg-muted" style={{ marginBottom: 8 }}>
          {t("struct.ars.rezumat_nivel", {
            b: n.b_cm, h: n.h_cm, H: n.H_m, n: n.armare.n, phi: n.armare.phi,
            p: (n.armare.p_efectiv * 100).toFixed(2),
            et: n.armare.etrieri.phi, pas: n.armare.etrieri.pas_critic_cm,
            lcrj: n.armare.l_cr_jos_m ?? n.armare.l_cr_m,
            lcrs: n.armare.l_cr_sus_m ?? n.armare.l_cr_m,
            pascur: n.armare.etrieri.pas_curent_cm,
            mh: fmt(n.m_cap_axe.dir_h), mb: fmt(n.m_cap_axe.dir_b),
          })}
        </div>
        <details style={{ marginBottom: 10 }}>
          <summary className="sg-muted" style={{ cursor: "pointer" }}>
            <strong>{t("struct.ars.citeste_tabelul_jos")}</strong>{" "}
            {t("struct.ars.cele_8_familii")}
          </summary>
          <div className="sg-muted" style={{ marginTop: 6, lineHeight: 1.5 }}>
            <p>
              {tx("struct.ars.fiecare_rand_compara")}
            </p>
            <p>{tx("struct.ars.b_1_p")}</p>
            <p className="sg-muted">
              {t("struct.ars.ceapa_nu_e_simetrica", {
                jos: fmt(Math.min(...(n.suprafata[String(Object.keys(n.suprafata)
                  .map(Number).sort((a, b) => a - b)[0])] || [[0]])
                  .map((p) => p[0])), 0),
                sus: fmt(Math.max(...(n.suprafata[String(Object.keys(n.suprafata)
                  .map(Number).sort((a, b) => a - b)[0])] || [[0]])
                  .map((p) => p[0])), 0),
              })}
            </p>
            <p>
              {tx("struct.ars.nud_explicatie", {
                lim: String((n.verificari.find((v) => v.nume.startsWith("νd"))?.nume
                  || "").replace("νd ≤ ", "")) || "0,50",
              })}
            </p>
            <p>
              <strong>{t("struct.ars.taietoarea_capabil_titlu")}</strong>{" "}
              {t("struct.ars.vezi_explicatia_detaliat")}
            </p>
            <p>
              <strong>{t("struct.ars.ierarhia_nodului_titlu")}</strong>{" "}
              {t("struct.ars.ierarhia_explicatie_a")}{" "}
              <strong>{t("struct.ars.efortul_nod")}</strong>{" "}
              {tx("struct.ars.ierarhia_explicatie_b")}
            </p>
            <p>
              <strong>{t("struct.ars.5_6_nodul")}</strong>{" "}
              {t("struct.ars.vezi_sectiunea_noduri")}
            </p>
            <p>
              <strong>{t("struct.ars.7_confinarea_wd")}</strong>{" "}
              {t("struct.ars.vezi_explicatia_sub_tabel")}
            </p>
            <p>
              <strong>{t("struct.ars.8_interaxul_barelor")}</strong>{" "}
              {t("struct.ars.interax_explicatie", {
                nb: n.armare.bare_pe_latura_b ?? "?", b: n.b_cm,
                nh: n.armare.bare_pe_latura_h ?? "?", h: n.h_cm,
                n: n.armare.n,
                procent: n.armare.n_cerut_de_procent
                  && n.armare.n_cerut_de_procent !== n.armare.n
                  ? t("struct.ars.procentul_armare_ar", {
                      p1: n.armare.n_cerut_de_procent })
                  : "",
                interax: n.armare.interax_bare_cm ?? "?",
              })}
            </p>
          </div>
        </details>
        {n.punct_vulnerabil && (
          <div className="sg-muted" style={{ marginBottom: 8 }}>
            {t("struct.ars.punct_vulnerabil", {
              loc: n.punct_vulnerabil.loc, combo: n.punct_vulnerabil.combo,
              N: n.punct_vulnerabil.N, M2: n.punct_vulnerabil.M2,
              M3: n.punct_vulnerabil.M3, re: n.punct_vulnerabil.RE,
              alpha: n.punct_vulnerabil.alpha_foaie,
              indice: n.punct_vulnerabil.indice_foaie ?? "—",
            })}
          </div>
        )}
        <table className="sg-table ver-tabel">
          <thead>
            <tr>
              <th>{t("struct.ars.th_verificarea_hover")}</th>
              <th>R/E</th><th>{t("struct.ars.th_verdict")}</th>
              <th>{t("struct.ars.detalii_referinta")}</th>
            </tr>
          </thead>
          <tbody>
            {n.verificari.map((v, i) => (
              <tr key={i}>
                <td>
                  <TitluVerificare catalog={catalog} pas="armare_stalpi"
                                   nume={v.nume} aratPasul={false} />
                </td>
                <td className="ver-re">{v.RE === null ? "—" : fmt(v.RE, 2)}</td>
                <td><Verdict v={v.verdict} /></td>
                <td className="sg-muted" style={{ fontSize: 12 }}>
                  {v.detaliu && Object.entries(v.detaliu)
                    .filter(([k, x]) => x !== null && k !== "recomandare" && typeof x !== "object")
                    .map(([k, x]) => `${k}=${typeof x === "number" ? x : String(x)}`)
                    .join(" · ")}
                  {v.detaliu && (v.detaliu as Record<string, unknown>).recomandare ? (
                    <div className="arm-warn" style={{ marginTop: 4 }}>
                      {String((v.detaliu as Record<string, unknown>).recomandare)}
                    </div>
                  ) : null}
                  {(() => {
                    const om = (v.detaliu as Record<string, unknown> | undefined)?.coef_amplificare;
                    return typeof om === "number" && om < 1 ? (
                      <div className="arm-warn" style={{ marginTop: 4 }}>
                        {t("struct.ars.omega_subunitar", { om })}
                      </div>
                    ) : null;
                  })()}
                  <div style={{ fontStyle: "italic" }}>{v.referinta}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="st-findings">
        <h3>{t("struct.ars.taietoarea_titlu")}</h3>
        <Pas titlu={t("struct.ars.explicatia_larg")}
             rezumat={t("struct.ars.taietoarea_rezumat")}>
        <div className="sg-muted" style={{ lineHeight: 1.55 }}>
          <p>
            {t("struct.ars.un_stalp_care_cedeaza")}{" "}
            <strong>{t("struct.ars.incovoiere")}</strong>{" "}
            {t("struct.ars.stalp_care_cedeaza_a")}{" "}
            <strong>{t("struct.ars.taietoare")}</strong>{" "}
            {tx("struct.ars.stalp_care_cedeaza_b")}
          </p>
          <p>
            {t("struct.ars.ea_spune_intreba")}
          </p>
          <div className="sg-mono" style={{ margin: "6px 0" }}>
            V_Ed = γ_Rd · (M_Rc,jos·k_jos + M_Rc,sus·k_sus) / H_liber
          </div>
          <p>{tx("struct.ars.b_rd_b")}</p>
          <p>
            {tx("struct.ars.k_este_plafon", {
              plafon: t("struct.ars.plafonul_echilibru_nodul2"),
              explicatie: t("struct.ars.plafonul_k_explicatie"),
            })}
          </p>
          <p>
            <strong>{t("struct.ars.m_rc_ia")}</strong>{" "}
            {t("struct.ars.mrc_compresiune")}
          </p>
          <p>
            <strong>{t("struct.ars.verifica_ambele_directii")}</strong>{" "}
            {t("struct.ars.doua_directii_explicatie", {
              min: n.armare.etrieri.nr_brate_min ?? 2,
              max: n.armare.etrieri.nr_brate_max ?? 2,
            })}
          </p>
        </div>
        </Pas>
        <table className="sg-table">
          <thead>
            <tr>
              <th>{t("struct.ars.directia")}</th><th>M_Rc [kNm]</th>
              <th title={t("struct.ars.plafonul_echilibru_nodul")}>k_jos / k_sus</th>
              <th>V_cap [kN]</th><th title={t("struct.ars.taietoarea_rezultata_ana")}>{t("struct.ars.v_analiza_kn")}</th>
              <th>V_Ed [kN]</th><th>V_Rd [kN]</th>
              <th title={t("struct.ars.latimea_inaltimea_utila")}>b × d [cm]</th>
              <th>{t("struct.ars.brate")}</th><th>R/E</th><th>{t("struct.fem.vf_col_verdict")}</th>
            </tr>
          </thead>
          <tbody>
            {n.verificari.filter((v) => v.nume.startsWith("Tăietoarea")).map((v, i) => {
              const d = (v.detaliu ?? {}) as Record<string, number | string>;
              return (
                <tr key={i}>
                  <td className="sg-mono">{String(d.directie ?? "?")}</td>
                  <td className="sg-mono">{String(d.M_Rc_knm ?? "—")}</td>
                  <td className="sg-mono">
                    {String(d.plafon_nod_jos ?? "—")} / {String(d.plafon_nod_sus ?? "—")}
                  </td>
                  <td className="sg-mono">{String(d.V_cap ?? "—")}</td>
                  <td className="sg-mono">{String(d.V_analiza ?? "—")}</td>
                  <td className="sg-mono"><strong>{String(d.V_Ed ?? "—")}</strong></td>
                  <td className="sg-mono">{String(d.V_Rd ?? "—")}</td>
                  <td className="sg-mono">
                    {String(d.b_sectiune_cm ?? "—")} × {String(d.d_util_cm ?? "—")}
                  </td>
                  <td className="sg-mono">{String(d.nr_brate_etrier ?? "—")}</td>
                  <td className="sg-mono">{v.RE === null ? "—" : fmt(v.RE, 2)}</td>
                  <td><Verdict v={v.verdict} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="sg-muted" style={{ marginTop: 6 }}>
          {t("struct.ars.ved_max_explicatie", {
            h: String(((n.verificari.find((v) => v.nume.startsWith("Tăietoarea"))
              ?.detaliu ?? {}) as Record<string, unknown>).H_liber_m ?? "—"),
          })}
        </div>
      </div>

      <div className="st-findings">
        <h3>{t("struct.ars.confinarea_titlu")}</h3>
        <Pas titlu={t("struct.ars.explicatia_larg")}
             rezumat={t("struct.ars.confinarea_rezumat")}>
        <div className="sg-muted" style={{ lineHeight: 1.55 }}>
          <p>
            {t("struct.ars.betonul_comprimat_a")}{" "}
            <strong>{t("struct.ars.umfla_lateral")}</strong>{" "}
            {t("struct.ars.betonul_comprimat_b")}{" "}
            <strong>{t("struct.ars.deformatie_mult_mare")}</strong>{" "}
            {t("struct.ars.betonul_comprimat_c")}
          </p>
          <p>
            {tx("struct.ars.miezul_confinat_explicatie", {
              b0: n.armare.etrieri.b0_cm ?? "?",
              h0: n.armare.etrieri.h0_cm ?? "?",
              b: n.b_cm, h: n.h_cm,
            })}
          </p>
          <p>
            {tx("struct.ars.cantitatea_confinare")}
          </p>
          <p>
            {tx("struct.ars.peste_minim_vine")}
          </p>
          <div className="sg-mono" style={{ margin: "6px 0" }}>
            α·ω_wd ≥ 30·μ_φ·ν_d·ε_sy,d·b_c/b₀ − 0,035
          </div>
          <p>
            {tx("struct.ars.muphi_explicatie")}{" "}
            <strong>{t("struct.ars.consecinta_practica")}</strong>{" "}
            {t("struct.ars.muphi_consecinta")}
          </p>
          <p>{tx("struct.ars.b_d_b")}</p>
          <p>{tx("struct.ars.b_n_s")}</p>
          <p>
            <strong>{t("struct.ars.pasul_etrierilor_zona")}</strong>{" "}
            {t("struct.ars.pasul_etrierilor_explicatie")}{" "}
            <strong>{t("struct.ars.s_8_bara")}</strong>{" "}
            {t("struct.ars.pasul_etrierilor_flambaj", {
              pas: n.armare.etrieri.pas_critic_cm,
            })}
          </p>
        </div>
        </Pas>
        <table className="sg-table">
          <tbody>
            <tr>
              <td>{t("struct.ars.miezul_confinat_b0h0")}</td>
              <td className="sg-mono">
                {t("struct.ars.val_cm_x_cm", {
                  a: n.armare.etrieri.b0_cm ?? "?",
                  b: n.armare.etrieri.h0_cm ?? "?",
                })}
              </td>
            </tr>
            <tr>
              <td>{t("struct.ars.etrieri")}</td>
              <td className="sg-mono">
                {t("struct.ars.val_etrieri", {
                  phi: n.armare.etrieri.phi, pas: n.armare.etrieri.pas_critic_cm,
                  min: n.armare.etrieri.nr_brate_min ?? 2,
                  max: n.armare.etrieri.nr_brate_max ?? 2,
                })}
              </td>
            </tr>
            <tr>
              <td>{t("struct.ars.rho_omega")}</td>
              <td className="sg-mono">
                {n.confinare.rho_w} / {n.confinare.omega_wd ?? "—"}
              </td>
            </tr>
            <tr>
              <td>{t("struct.ars.eficienta_confinarii")}</td>
              <td className="sg-mono">{n.confinare.alpha ?? "—"}</td>
            </tr>
            <tr>
              <td>{t("struct.ars.betonul_confinat_rezultat")}</td>
              <td className="sg-mono">
                f_cc = {n.confinare.f_cc_mpa} MPa · ε_cu,c = {n.confinare.eps_cu_c}
              </td>
            </tr>
          </tbody>
        </table>
        <div className="sg-muted" style={{ marginTop: 6 }}>
          {t("struct.ars.betonul_confinat_f")}
        </div>
      </div>

      <CoajaInaltime s={s} />

      <PlanNoduri s={s} />

      <div className="st-findings">
        <h3>{t("struct.ars.confinarea_model_titlu")}</h3>
        <div className="sg-muted">
          {t("struct.ars.confinarea_model_text", {
            model: n.confinare.model, rho: n.confinare.rho_w,
            fl: n.confinare.f_l_mpa, fcc: n.confinare.f_cc_mpa,
            eps: n.confinare.eps_cu_c, nota: n.confinare.nota,
          })}
        </div>
      </div>

      <div className="st-findings">
        <div className="sg-muted" style={{ lineHeight: 1.5 }}>
          {t("struct.ars.cautarea_armarii")}
          {s.nr_incercari != null
            ? t("struct.ars.aici_a_luat", {
                n: tn("struct.ars.incercari#n", s.nr_incercari),
              })
            : ""}{" "}
          {t("struct.ars.jurnalul_nu_se_pastreaza")}
        </div>
      </div>
    </>
  );
}

// „Coaja pe înălțime" (docx §5.3): capacitatea per nivel trebuie să
// îmbrace momentul efectiv maxim per nivel.
function CoajaInaltime({ s }: { s: StalpArmat }) {
  const W = 560, H = 60 + s.nivele.length * 64, mx = 60;
  const mMax = Math.max(...s.nivele.map((n) =>
    Math.max(n.m_cap_axe.dir_h, n.m_cap_axe.dir_b,
             ...n.puncte.map((pt) => Math.hypot(pt[1], pt[2])))), 1);
  const sx = (m: number) => mx + (m / mMax) * (W - mx - 20);
  return (
    <div className="st-findings">
      <h3>{t("struct.ars.coaja_titlu")}</h3>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: 620, background: "#fff", border: "1px solid #dedede", borderRadius: 6 }}>
        {s.nivele.map((n, i) => {
          const y = H - 30 - i * 64;
          const mEf = Math.max(...n.puncte.map((pt) => Math.hypot(pt[1], pt[2])));
          const mCap = Math.min(n.m_cap_axe.dir_h, n.m_cap_axe.dir_b);
          return (
            <g key={i}>
              <text x={4} y={y + 4} fontSize={11} fill="#555">{n.id_plan}</text>
              <rect x={mx} y={y - 18} width={sx(mCap) - mx} height={14} fill="#2e7d4f33" stroke="#4f8207" />
              <rect x={mx} y={y + 2} width={sx(mEf) - mx} height={14} fill="#b3362d33" stroke="#b5323d" />
              <text x={sx(mCap) + 4} y={y - 7} fontSize={10} fill="#4f8207">M_cap {fmt(mCap)} kNm</text>
              <text x={sx(mEf) + 4} y={y + 13} fontSize={10} fill="#b5323d">M_ef,max {fmt(mEf)} kNm</text>
            </g>
          );
        })}
        <text x={mx} y={16} fontSize={12} fill="#555">{t("struct.ars.coaja_legenda")}</text>
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dispunerea barelor pe perimetru — replică 1:1 a lui
// sectiune_fibre.bare_perimetrale (colțurile mereu ocupate): ce se desenează
// e ce a intrat în motorul de fibre. Returnează (y, z) în cm: y pe latura b,
// z pe latura h, cu originea în centrul secțiunii.
// ---------------------------------------------------------------------------

function barePerimetrale(b: number, h: number, nTotal: number, c: number): [number, number][] {
  const perLat = Math.max(2, Math.floor(nTotal / 4) + 1);
  const lin = (L: number) =>
    Array.from({ length: perLat }, (_, i) => -L / 2 + c + ((L - 2 * c) * i) / (perLat - 1));
  const out: [number, number][] = [];
  for (const y of lin(b)) {
    for (const z of lin(h)) {
      const peContur = Math.abs(Math.abs(y) - (b / 2 - c)) < 1e-6
        || Math.abs(Math.abs(z) - (h / 2 - c)) < 1e-6;
      if (peContur) out.push([y, z]);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Secțiunea transversală 2D (SVG) — latura h pe orizontală (ca în plan)
// ---------------------------------------------------------------------------

export function SectiuneStalp({ n, cCm }: { n: NivelStalp; cCm: number }) {
  useT();
  const b = n.b_cm, h = n.h_cm;
  const sc = Math.min(3.2, 210 / Math.max(b, h));
  // caseta face loc cotelor: două raiuri la stânga (b, interaxul) și una
  // dedesubt (h), plus etichetele de armare la dreapta
  const ox = 132, oy = 48;
  const W = ox + h * sc + 196, Hh = oy + b * sc + 62;
  const bare = barePerimetrale(b, h, n.armare.n, cCm);
  const px = (z: number) => ox + (z + h / 2) * sc;
  const py = (y: number) => oy + (y + b / 2) * sc;
  // interaxul barelor pe latura h — regula celor 18 cm, cotată
  const zUnice = [...new Set(bare.map(([, z]) => z))].sort((a, c) => a - c);
  return (
    <svg viewBox={`0 0 ${W} ${Hh}`} style={{ width: "100%", maxWidth: W, background: "#fff", border: "1px solid #dedede", borderRadius: 6 }}>
      <text x={4} y={14} fontSize={12} fill="#555">
        {t("struct.ars.sectiunea_titlu", {
          id: n.id_plan, b, h,
          alungit: n.dir ? t("struct.ars.sectiunea_alungita") : "",
        })}
      </text>
      <rect x={ox} y={oy} width={h * sc} height={b * sc} fill="#ececec" stroke="#737373" />
      <rect x={ox + (cCm - 0.8) * sc} y={oy + (cCm - 0.8) * sc}
            width={(h - 2 * cCm + 1.6) * sc} height={(b - 2 * cCm + 1.6) * sc}
            fill="none" stroke="#232323" strokeWidth={1.4} rx={4} />
      {bare.map(([y, z], i) => (
        <circle key={i} cx={px(z)} cy={py(y)}
                r={Math.max(2, (n.armare.phi / 20) * sc)} fill="#b5323d" />
      ))}
      {/* COTELE: h dedesubt, b la stânga, acoperirea și interaxul lipite de
          muchia lor */}
      <CotaH x1={ox} x2={ox + h * sc} y={oy + b * sc + 24}
             martorY={oy + b * sc} text={`h = ${cmFmt(h)} cm`} />
      <CotaV x={ox - 26} y1={oy} y2={oy + b * sc} martorX={ox}
             text={`b = ${cmFmt(b)} cm`} />
      <CotaV x={ox - 66} y1={oy} y2={oy + (cCm - 0.8) * sc} martorX={ox}
             text={`c = ${cmFmt(cCm)} cm`} size={9} />
      {zUnice.length > 1 && (
        <CotaH x1={px(zUnice[0])} x2={px(zUnice[1])} y={oy - 14}
               martorY={oy} deasupra
               text={`${(n.armare.interax_bare_cm ?? Math.abs(zUnice[1] - zUnice[0])).toFixed(1)}`}
               size={9} />
      )}
      <text x={ox + h * sc + 14} y={oy + 12} fontSize={11} fill="#b5323d">
        {n.armare.n}Ø{n.armare.phi} pe perimetru
      </text>
      <text x={ox + h * sc + 14} y={oy + 28} fontSize={11} fill="#232323">
        etr. Ø{n.armare.etrieri.phi}/{n.armare.etrieri.pas_critic_cm} (l_cr) /{n.armare.etrieri.pas_curent_cm} cm
      </text>
      <text x={ox + h * sc + 14} y={oy + 44} fontSize={11} fill="#777">
        p = {(n.armare.p_efectiv * 100).toFixed(2)}% · c = {cCm} cm
      </text>
      <text x={ox + h * sc + 14} y={oy + 60} fontSize={10} fill="#737373">
        {t("struct.ars.interax_18_cm")}
      </text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Carcasa 3D — stâlpul pe toată verticala: bare longitudinale, etrieri cu
// îndesirile în l_cr, grinzile din nod schițate (colorate după verdictul
// verificărilor de nod ale nivelului)
// ---------------------------------------------------------------------------

export function Stalp3D({ s, cCm, nivele, fundatie }: {
  s: StalpArmat;
  cCm: number;
  // nivelurile DESENATE (implicit toată stiva).
  // Comutatorul din pagina pasului trimite aici doar nivelul selectat.
  nivele?: NivelStalp[];
  // geometria REALĂ a fundației: până pe 26.08 se desena o cutie fixă de
  // 1,60×1,60×0,40 la z = −0,20, aceeași la orice proiect, deși adâncimea de
  // fundare e de obicei 0,9–1,8 m
  fundatie?: FundatieStalp | null;
}) {
  const mount = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = mount.current;
    if (!el) return;
    const W = el.clientWidth || 900, H = 420;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf3f6f9);
    const camera = new THREE.PerspectiveCamera(40, W / H, 0.01, 200);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    el.appendChild(renderer.domElement);

    const gri = (op: number, col = 0x9aa0a8) =>
      new THREE.MeshStandardMaterial({ color: col, transparent: true, opacity: op, depthWrite: false });
    const cM = (cCm - 0.8) / 100;   // etrierul stă în afara barelor
    // ce se desenează: felia cerută, dar la cotele ei ADEVĂRATE pe verticală
    const vrute = new Set((nivele ?? s.nivele).map((x) => x.id_plan));
    const cerute = s.nivele.filter((x) => vrute.has(x.id_plan));
    // o felie care nu se regăsește în stivă (id-uri dintr-o rulare veche) nu
    // are voie să golească scena: se cade pe stiva întreagă
    const desen = cerute.length ? cerute : s.nivele;
    const iPrim = s.nivele.findIndex((x) => x.id_plan === desen[0].id_plan);
    const zStart = s.nivele
      .slice(0, Math.max(iPrim, 0))
      .reduce((a, x) => a + x.H_m, 0);
    const totalH = desen.reduce((a, x) => a + x.H_m, 0);
    // fundația se arată numai când e desenată baza stivei (altfel ar pluti
    // sub un nivel de la etaj)
    const cuFundatie = Boolean(fundatie) && desen[0].id_plan === s.nivele[0]?.id_plan;

    let z0 = zStart;
    for (const n of desen) {
      const b = n.b_cm / 100, h = n.h_cm / 100, Hn = n.H_m;
      const th = n.dir ? Math.atan2(n.dir[1], n.dir[0]) : 0;
      const rot = (lx: number, ly: number): [number, number] => [
        lx * Math.cos(th) - ly * Math.sin(th),
        lx * Math.sin(th) + ly * Math.cos(th),
      ];

      // betonul nivelului (transparent) + muchiile
      const box = new THREE.Mesh(new THREE.BoxGeometry(h, b, Hn), gri(0.16));
      box.rotation.z = th;
      box.position.set(0, 0, z0 + Hn / 2);
      scene.add(box);
      const edges = new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.BoxGeometry(h, b, Hn)),
        new THREE.LineBasicMaterial({ color: 0x777777 }));
      edges.rotation.z = th;
      edges.position.set(0, 0, z0 + Hn / 2);
      scene.add(edges);

      // barele longitudinale — aceleași poziții ca în motorul de fibre
      for (const [y, z] of barePerimetrale(n.b_cm, n.h_cm, n.armare.n, cCm)) {
        const [gx, gy] = rot(z / 100, y / 100);
        const geo = new THREE.CylinderGeometry(n.armare.phi / 2000, n.armare.phi / 2000, Hn, 8);
        const m = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: 0xc0392b }));
        m.rotation.x = Math.PI / 2;
        m.position.set(gx, gy, z0 + Hn / 2);
        scene.add(m);
      }

      // etrierii: pas critic în zonele critice — mai lungă la BAZĂ (1,5·h_c,
      // P100-1 (5.22)) decât sub nod (h_c, (5.23)) —, curent între ele; pe
      // lungimea petrecerii pasul scade și mai mult (§5.7.3(5))
      const lcr = n.armare.l_cr_jos_m ?? n.armare.l_cr_m;
      const lcrSus = n.armare.l_cr_sus_m ?? n.armare.l_cr_m;
      const pet = n.petrecere;
      const pasP = (n.armare.etrieri.pas_innadire_cm ?? 0) / 100;
      const pasC = n.armare.etrieri.pas_critic_cm / 100;
      const pasU = n.armare.etrieri.pas_curent_cm / 100;
      const hx = h / 2 - cM, by = b / 2 - cM;
      const corners = ([[hx, by], [-hx, by], [-hx, -by], [hx, -by]] as [number, number][])
        .map(([lx, ly]) => rot(lx, ly));
      const pts: number[] = [];
      let z = z0 + 0.04;
      while (z < z0 + Hn - 0.03) {
        for (let i = 0; i < 4; i++) {
          const [x1, y1] = corners[i], [x2, y2] = corners[(i + 1) % 4];
          pts.push(x1, y1, z, x2, y2, z);
        }
        const dz = z - z0;
        const inPetrecere = !!pet && pasP > 0
          && dz >= pet.z_m && dz <= pet.z_m + pet.l0_m;
        z += inPetrecere ? pasP
          : (dz < lcr || dz > Hn - lcrSus) ? pasC : pasU;
      }
      const geoE = new THREE.BufferGeometry();
      geoE.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
      scene.add(new THREE.LineSegments(geoE, new THREE.LineBasicMaterial({ color: 0x2c3e70 })));

      // Nodul de la vârf: FIECARE GRINDĂ care intră în el, pe partea ei,
      // colorată după verdictul verificărilor de nod ale nivelului.
      //
      // DIRECȚIILE nu se deduc aici din verificările de
      // ierarhie și se desena, pe fiecare, o grindă CONTINUĂ prin stâlp —
      // deci două brațe pentru o singură grindă, iar la ultimul nivel (unde
      // ierarhia e exceptată de P100-1 §5.2.3.3.2) se presupuneau ambele
      // direcții. Un stâlp de colț cu două grinzi apărea cu patru brațe.
      // Acum partea vine din rezultat (`grinzi_nod`), din aceeași convenție
      // de capete din care verificarea de forfecare scoate «nod interior».
      // Rulările vechi n-o poartă: nodul rămâne NEdesenat, fiindcă direcția
      // singură nu spune pe ce parte intră grinda.
      const nodBad = n.verificari.some(
        (v) => (v.nume.startsWith("Nod:") || v.nume.startsWith("Ierarhia"))
          && v.verdict.startsWith("NU"));
      const colNod = nodBad ? 0xe04b3a : 0x1d7a4b;
      // axa fiecărei familii, în planul ROTIT al stâlpului: latura h e de-a
      // lungul lui `dir` (aceeași convenție cu `_familia` din armare_stalpi),
      // iar sensul + al familiei e cel cu proiecție pozitivă pe axa globală
      const famH = n.dir && Math.abs(n.dir[0]) < Math.abs(n.dir[1]) ? "Y" : "X";
      const axaFam = (fam: string): { u: [number, number]; semi: number } => {
        const u: [number, number] = fam === famH
          ? [Math.cos(th), Math.sin(th)]
          : [-Math.sin(th), Math.cos(th)];
        const comp = fam === "X" ? u[0] : u[1];
        return {
          u: comp < 0 ? [-u[0], -u[1]] : u,
          semi: (fam === famH ? h : b) / 2,   // de la ax la FAȚA stâlpului
        };
      };
      for (const g of n.grinzi_nod ?? []) {
        const { u, semi } = axaFam(g.directie);
        const sgn = g.parte >= 0 ? 1 : -1;
        const bg = g.b_m > 0.01 ? g.b_m : 0.25;
        const hg = g.h_m > 0.01 ? g.h_m : 0.5;
        // ciotul pornește de la fața stâlpului; lungimea lui e o schiță de
        // context, plafonată ca să nu iasă clădirea din cadru
        const lg = Math.min(Math.max(g.L_m || 1.6, 0.6), 1.8);
        const gm = new THREE.Mesh(new THREE.BoxGeometry(lg, bg, hg),
                                  gri(0.22, colNod));
        gm.rotation.z = Math.atan2(sgn * u[1], sgn * u[0]);
        gm.position.set(sgn * u[0] * (semi + lg / 2),
                        sgn * u[1] * (semi + lg / 2),
                        z0 + Hn - hg / 2);
        scene.add(gm);
      }
      z0 += Hn;
    }

    // FUNDAȚIA, la geometria ei reală (Pasul 6): inimă + talpă, la adâncimea
    // de fundare a axului pe care stă stâlpul. Cutia fixă de dinainte arăta
    // 0,40 m acolo unde talpa e, de regulă, la 0,9–1,8 m.
    if (cuFundatie && fundatie) {
      const hT = fundatie.hTalpa_m;
      const hI = Math.max(fundatie.hInima_m, 0.05);
      const bI = fundatie.bInima_m;
      // betonul nu pornește de la fața de jos a plăcii: deasupra lui stă
      // umplutura de h_peste (la proiectul 2 sunt 0,30 m)
      const zSus = -fundatie.hPeste_m;
      const inima = new THREE.Mesh(
        new THREE.BoxGeometry(bI, bI, hI), gri(0.22, 0x7d858f));
      inima.position.set(0, 0, zSus - hI / 2);
      scene.add(inima);
      const talpa = new THREE.Mesh(
        new THREE.BoxGeometry(fundatie.B_m, fundatie.B_m, hT),
        gri(0.28, 0x7d858f));
      talpa.position.set(0, 0, zSus - hI - hT / 2);
      scene.add(talpa);
      for (const [geo, z] of [
        [new THREE.BoxGeometry(bI, bI, hI), zSus - hI / 2] as const,
        [new THREE.BoxGeometry(fundatie.B_m, fundatie.B_m, hT), zSus - hI - hT / 2] as const,
      ]) {
        const m = new THREE.LineSegments(
          new THREE.EdgesGeometry(geo),
          new THREE.LineBasicMaterial({ color: 0x76848a }));
        m.position.set(0, 0, z);
        scene.add(m);
      }
    }

    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const dl = new THREE.DirectionalLight(0xffffff, 0.7);
    dl.position.set(3, -4, 6);
    scene.add(dl);

    camera.position.set(totalH * 0.55 + 1.4, -(totalH * 0.75 + 1.8),
                        zStart + totalH * 0.75);
    camera.up.set(0, 0, 1);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 0, zStart + totalH / 2);
    const render = () => renderer.render(scene, camera);
    controls.addEventListener("change", render);
    // distanța reală o pune încadrarea, după ce scena e gata (un stack înalt
    // cu grinzile din noduri ieșea din cadru)
    incadreaza(camera, controls, scene);
    render();
    return () => {
      controls.removeEventListener("change", render);
      controls.dispose();
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
  }, [s, cCm, nivele, fundatie]);
  return <div ref={mount} style={{ width: "100%", height: 420 }} />;
}

// ---------------------------------------------------------------------------
// Planul pentru noduri — starea per nod + ce e implementat / ce urmează
// ---------------------------------------------------------------------------

function PlanNoduri({ s }: { s: StalpArmat }) {
  useT();
  const celula = (v: { RE: number | null; verdict: string } | undefined, gol: string) =>
    v ? (
      <>
        <span className="sg-mono">{v.RE === null ? "—" : fmt(v.RE, 2)}</span>{" "}
        <Verdict v={v.verdict} />
      </>
    ) : (
      <span className="sg-muted">{gol}</span>
    );
  return (
    <div className="st-findings">
      <h3>{t("struct.ars.nodurile_titlu")}</h3>
      <div className="sg-muted" style={{ marginBottom: 8 }}>
        {t("struct.ars.nodul_rigid_a")}{" "}
        <strong>{t("struct.ars.ierarhia_cuvant")}</strong>{" "}
        {t("struct.ars.nodul_rigid_b")}{" "}
        <strong>{t("struct.ars.forfecarea_orizontala")}</strong>{" "}
        {t("struct.ars.nodul_rigid_c")}{" "}
        <strong>{t("struct.ars.ancorarea_barelor_grinzii")}</strong>{" "}
        {t("struct.ars.nodul_rigid_d")}
      </div>
      <div className="sg-muted" style={{ marginBottom: 6, lineHeight: 1.55 }}>
        <p>
          <strong>{t("struct.ars.toate_trei_directii_titlu")}</strong>
          {tx("struct.ars.toate_trei_directii_text")}
        </p>
        <p>
          <strong>{t("struct.ars.nodul_cel_solicitat")}</strong> {t("struct.ars.cutremur_intr_nod")}
        </p>
        <p>
          <strong>{t("struct.ars.ancorarea_barelor_limite")}</strong> {t("struct.ars.bara_continua_printr")}
        </p>
      </div>
      <table className="sg-table">
        <thead>
          <tr>
            <th>{t("struct.ars.nodul_varful_nivelului")}</th>
            <th title={t("struct.ars.efortul_nod_m")}>{t("struct.ars.th_ierarhia")}</th>
            <th>{t("struct.ars.th_forfecare_diagonala")}</th>
            <th>{t("struct.ars.th_ancorarea_barelor")}</th>
          </tr>
        </thead>
        <tbody>
          {s.nivele.map((n, i) => {
            const ier = n.verificari.filter((v) => v.nume.startsWith("Ierarhia"));
            // per direcție (X / Y) — NU .find(): un nod cu grinzi pe ambele
            // direcții are câte o verificare pentru fiecare, iar cea scurtă
            // e de obicei cea care guvernează
            const forf = n.verificari.filter((v) => v.nume.startsWith("Nod: forfecare"));
            const anc = n.verificari.filter((v) => v.nume.startsWith("Nod: ancorarea"));
            const ultim = i === s.nivele.length - 1;
            return (
              <tr key={i}>
                <td className="sg-mono">{n.id_plan} ({n.nivel})</td>
                <td>
                  {ier.length ? ier.map((v, j) => {
                    const om = (v.detaliu as Record<string, unknown>)?.coef_amplificare;
                    return (
                      <div key={j}>
                        {String((v.detaliu as Record<string, unknown>)?.directie ?? "?")}:{" "}
                        {celula(v, "")}
                        {typeof om === "number" ? (
                          <span
                            className="sg-muted sg-mono"
                            title={t("struct.ars.m_rb_m")}
                          >
                            {" "}· Ω={om}{om < 1 ? t("struct.ars.folosit_1") : ""}
                          </span>
                        ) : null}
                      </div>
                    );
                  }) : (
                    <span className="sg-muted">
                      {t(ultim && forf.length
                        ? "struct.ars.exceptat_ultimul_nivel"
                        : "struct.ars.fara_grinzi_in_nod")}
                    </span>
                  )}
                </td>
                <td>
                  {forf.length ? forf.map((v, j) => (
                    <div key={j}>
                      {String((v.detaliu as Record<string, unknown>)?.directie ?? "?")}
                      {" (h_c "}
                      {String((v.detaliu as Record<string, unknown>)?.h_c_cm ?? "?")}
                      {" cm): "}
                      {celula(v, "")}
                    </div>
                  )) : <span className="sg-muted">{t("struct.ars.fara_grinzi_nod")}</span>}
                </td>
                <td>
                  {anc.length ? anc.map((v, j) => (
                    <div key={j}>
                      {String((v.detaliu as Record<string, unknown>)?.directie ?? "?")}
                      {" (h_c "}
                      {String((v.detaliu as Record<string, unknown>)?.h_c_cm ?? "?")}
                      {" cm, Ø"}
                      {String((v.detaliu as Record<string, unknown>)?.phi_max_grinda_mm ?? "?")}
                      {" ≤ Ø"}
                      {String((v.detaliu as Record<string, unknown>)?.phi_admis_mm ?? "?")}
                      {"): "}
                      {celula(v, "")}
                    </div>
                  )) : <span className="sg-muted">{t("struct.ars.fara_grinzi_nod")}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <details style={{ marginTop: 8 }}>
        <summary className="sg-muted" style={{ cursor: "pointer" }}>
          {t("struct.ars.ce_urmeaza_noduri")}
        </summary>
        <ul className="sg-muted" style={{ marginTop: 4 }}>
          <li>{tx("struct.ars.b_etrierii_orizontali")}</li>
          <li>{tx("struct.ars.b_v_c")}</li>
          <li>{tx("struct.ars.verificarile_b_geometric")}</li>
          <li>
            <strong>{t("struct.ars.noduri_picate_evidentiate")}</strong>{" "}
            {t("struct.ars.noduri_picate_acum")}
          </li>
        </ul>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------
// „Ceapa" 3D — suprafața P–M–M + punctele combinațiilor
// ---------------------------------------------------------------------------

// Ceapa citește doar suprafața și punctele, deci o desenează la fel pentru
// un nivel de stâlp și pentru unul de lamelă (`armare_lamele._ceapa` scrie
// aceleași chei, în aceleași axe).
export function Ceapa3D({ n }: {
  n: Pick<NivelStalp, "suprafata" | "puncte">;
}) {
  useT();
  const mount = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = mount.current;
    if (!el) return;
    const W = el.clientWidth || 900, H = 360;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf3f6f9);
    const camera = new THREE.PerspectiveCamera(40, W / H, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(W, H);
    el.appendChild(renderer.domElement);

    const grade = Object.keys(n.suprafata).map(Number).sort((a, b) => a - b);
    const mScale = Math.max(...grade.map((g) =>
      Math.max(...n.suprafata[String(g)].map((p) => Math.abs(p[1])))), 1);
    const nVals = n.suprafata[String(grade[0])].map((p) => p[0]);
    const nMin = Math.min(...nVals), nMax = Math.max(...nVals);
    const sM = 1.2 / mScale, sN = 2.0 / (nMax - nMin || 1);
    // Axa N trece prin ZERO: z = N·sN, deci z = 0 ⇔ N = 0. Până în august
    // 2026 aici era (N − nMin)·sN − 1, adică intervalul întins pe [−1, +1] cu
    // originea desenată la MIJLOCUL lui — care cade fix pe punctul balansat.
    // Ceapa ieșea așa simetrică sus-jos față de cruce oricare ar fi armarea,
    // sugerând (fals) că betonul duce la întindere cât la compresiune.
    const zN = (Nv: number) => Nv * sN;
    const zT = zN(nMin), zC = zN(nMax), zMid = 0.5 * (zT + zC);
    const gunoi: { dispose(): void }[] = [];

    // momentul capabil la un N dat, pe o curbă stocată (maximul peste
    // intervalele care îl conțin — vezi m_capabil din sectiune_fibre.py)
    const mLaN = (curba: number[][], Nv: number) => {
      let best = 0;
      for (let i = 0; i < curba.length - 1; i++) {
        const [n1, m1] = curba[i], [n2, m2] = curba[i + 1];
        if (n1 <= Nv && Nv <= n2) {
          const f = n2 - n1 < 1e-9 ? 0 : (Nv - n1) / (n2 - n1);
          best = Math.max(best, Math.abs(m1 + f * (m2 - m1)));
        }
      }
      return best;
    };

    // suprafața: vertecși per (theta, punct) — curbele au același număr
    // de puncte, ordonate pe N crescător
    const rows = n.suprafata[String(grade[0])].length;
    const verts: number[] = [];
    const idx: number[] = [];
    const nTh = grade.length;
    for (let t = 0; t < nTh; t++) {
      const th = (grade[t] * Math.PI) / 180;
      const curba = n.suprafata[String(grade[t])];
      for (let i = 0; i < rows; i++) {
        const [Nv, M] = curba[Math.min(i, curba.length - 1)];
        const rad = Math.abs(M) * sM;
        verts.push(rad * Math.cos(th), rad * Math.sin(th), zN(Nv));
      }
    }
    for (let t = 0; t < nTh; t++) {
      const t2 = (t + 1) % nTh;
      for (let i = 0; i < rows - 1; i++) {
        const a = t * rows + i, b = t2 * rows + i;
        idx.push(a, b, a + 1, b, b + 1, a + 1);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
      color: 0x1d7a4b, transparent: true, opacity: 0.35,
      side: THREE.DoubleSide, depthWrite: false,
    }));
    scene.add(mesh);
    scene.add(new THREE.LineSegments(new THREE.WireframeGeometry(geo),
      new THREE.LineBasicMaterial({ color: 0x1d7a4b, transparent: true, opacity: 0.25 })));

    // Punctele de încărcare, în ACELEAȘI axe cu suprafața: x = momentul pe
    // direcția θ = 0 (latura h), y = cel pe θ = 90 (latura b). Backend-ul le
    // trimite ca [N, M_pe_latura_b, M_pe_latura_h] — vezi puncte_3d din
    // armare_stalpi.py. Înainte trimitea M2/M3 brute și desenul le rotea cu
    // 90° față de ceapă.
    for (const [Nv, mB, mH] of n.puncte) {
      const p = new THREE.Mesh(new THREE.SphereGeometry(0.02, 8, 8),
        new THREE.MeshStandardMaterial({ color: 0xc0392b }));
      p.position.set(mH * sM, mB * sM, zN(Nv));
      scene.add(p);
    }
    // axele momentelor — desenate în PLANUL N = 0 (z = 0)
    const ax = (x: number, y: number, z: number, col: number,
                de: [number, number, number] = [0, 0, 0]) => {
      const g2 = new THREE.BufferGeometry();
      g2.setAttribute("position", new THREE.Float32BufferAttribute(
        [...de, x, y, z], 3));
      scene.add(new THREE.Line(g2, new THREE.LineBasicMaterial({ color: col })));
    };
    ax(1.4, 0, 0, 0x555555); ax(0, 1.4, 0, 0x555555);
    // axa N, de la întindere pură la compresiune pură, cu zeroul pe ea
    ax(0, 0, zC, 0x2c3e70, [0, 0, zT]);

    // conturul cepei TĂIAT la N = 0: reperul care arată unde e zeroul și cât
    // de mult peste el se umflă capacitatea
    const inel: number[] = [];
    for (const g of grade) {
      const r = mLaN(n.suprafata[String(g)], 0) * sM;
      const th = (g * Math.PI) / 180;
      inel.push(r * Math.cos(th), r * Math.sin(th), 0);
    }
    const gInel = new THREE.BufferGeometry();
    gInel.setAttribute("position", new THREE.Float32BufferAttribute(inel, 3));
    scene.add(new THREE.LineLoop(gInel, new THREE.LineBasicMaterial({
      color: 0x2c3e70, transparent: true, opacity: 0.9,
    })));

    // etichete numerice pe axa N (fără ele nu se poate citi unde e zeroul)
    const eticheta = (txt: string, x: number, y: number, z: number,
                      col = "#232323") => {
      const cv = document.createElement("canvas");
      cv.width = 320; cv.height = 64;
      const g2 = cv.getContext("2d");
      if (!g2) return;
      g2.fillStyle = col;
      g2.font = "bold 34px system-ui, sans-serif";
      g2.textAlign = "center"; g2.textBaseline = "middle";
      g2.fillText(txt, 160, 32);
      const tex = new THREE.CanvasTexture(cv);
      const mat = new THREE.SpriteMaterial({ map: tex, transparent: true });
      const sp = new THREE.Sprite(mat);
      sp.position.set(x, y, z);
      sp.scale.set(0.62, 0.124, 1);
      scene.add(sp);
      gunoi.push(tex, mat);
    };
    eticheta(t("struct.ars.n_p1_kn", { p1: Math.round(nMax) }), 0, 0, zC + 0.1);
    eticheta("N = 0", 0.28, -0.18, 0.06, "#b5323d");
    eticheta(t("struct.ars.n_p1_kn2", { p1: Math.round(nMin) }), 0, 0, zT - 0.1);
    eticheta(t("struct.ars.m_pe_latura", { l: "h" }), 1.45, 0, -0.09, "#737373");
    eticheta(t("struct.ars.m_pe_latura", { l: "b" }), 0, 1.45, -0.09, "#737373");

    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const dl = new THREE.DirectionalLight(0xffffff, 0.6);
    dl.position.set(3, 2, 4);
    scene.add(dl);
    camera.position.set(2.4, -2.4, zMid + 1.4);
    camera.up.set(0, 0, 1);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 0, zMid);
    const render = () => renderer.render(scene, camera);
    controls.addEventListener("change", render);
    // încadrarea prinde și etichetele N/M din vârfuri, nu doar suprafața
    incadreaza(camera, controls, scene);
    render();
    return () => {
      controls.removeEventListener("change", render);
      controls.dispose();
      gunoi.forEach((x) => x.dispose());
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
  }, [n]);
  return <div ref={mount} style={{ width: "100%", height: 360 }} />;
}

// Tipul sistemului pe direcții — și PROCESUL prin care s-a măsurat.
//
// De clasificarea asta atârnă o cerință de siguranță: ierarhia nodului
// (ΣM_Rc ≥ γ_Rd·ΣM_Eb) e regula CADRELOR și a sistemelor duale cu cadre
// predominante; la pereți predominanți nodul de cadru nu e mecanismul care
// disipează, deci normativul nu o cere. Cine citește un stâlp fără verdict de
// ierarhie trebuie să vadă aici de ce — cu cifrele, nu cu o afirmație.
function SistemPeDirectii({ s }: { s?: ArmareStalpiResult["sistem_directii"] }) {
  useT();
  if (!s || (!s.X && !s.Y)) return null;
  const dirs: ["X" | "Y", SistemDirectie | undefined][] = [["X", s.X], ["Y", s.Y]];
  const masurat = (s.X ?? s.Y)?.V_baza_kn != null;
  return (
    <div className="st-findings">
      <h3>{t("struct.ars.sistemul_titlu")}</h3>
      <p className="sg-muted">
        {t("struct.ars.clasifica_sistemul")}{" "}
        <strong>{t("struct.ars.directie")}</strong>
        {t("struct.ars.dupa_cat_din")}{" "}
        <strong>{t("struct.ars.taietoarea_baza")}</strong>{" "}
        {t("struct.ars.preiau_peretii")}{" "}
        <strong>{t("struct.ars.cere")}</strong>
        {t("struct.ars.regula_cadrelor")}
      </p>
      <ol className="sg-muted st-pasi-masurare">
        <li>{tx("struct.ars.b_perete_b")}</li>
        <li>
          <strong>{t("struct.ars.nivel")}</strong>
          {t("struct.ars.nivelul_baza_text", { n: (s.nivel_baza ?? 0) + 1 })}
        </li>
        {masurat ? (
          <>
            <li>
              <strong>{t("struct.ars.taietoarea_masurata_titlu")}</strong>{" "}
              {t("struct.ars.taietoarea_masurata_text")}
            </li>
            <li>
              <strong>{t("struct.ars.fractiunea")}</strong> = V<sub>{t("struct.ars.pereti")}</sub> / V<sub>{t("struct.ars.baza")}</sub>,
              {t("struct.ars.ambele_cqc")}
              V<sub>{t("struct.ars.pereti")}</sub> + V<sub>{t("struct.ars.restul")}</sub>{" "}
              {t("struct.ars.cqc_nu_e_aditiv")}{" "}
              V<sub>{t("struct.ars.baza")}</sub>
              {t("struct.ars.diferenta_in_tabel")}
            </li>
          </>
        ) : (
          <li>
            <strong>{t("struct.ars.pasul_9_lipseste")}</strong>
            {t("struct.ars.pasul9_lipseste_text")}
          </li>
        )}
      </ol>
      <table className="st-tabel">
        <thead>
          <tr>
            <th>{t("struct.ars.directia2")}</th>
            <th>{t("struct.ars.th_elemente_perete")}</th>
            {masurat ? (
              <>
                <th>V<sub>{t("struct.ars.pereti")}</sub> (kN)</th>
                <th>V<sub>{t("struct.ars.sub_restul")}</sub> (kN)</th>
                <th>V<sub>{t("struct.ars.baza")}</sub> (kN)</th>
                <th title={t("struct.ars.v_pereti_v")}>
                  {t("struct.ars.neaditivitate_cqc")}
                </th>
              </>
            ) : (
              <>
                <th>{t("struct.ars.i_pereti_m4")}</th>
                <th>ΣI total (m⁴)</th>
              </>
            )}
            <th>{t("struct.ars.fractiune")}</th>
            <th>{t("struct.rzv.th_prag")}</th>
            <th>{t("struct.ars.th_sistem")}</th>
            <th>{t("struct.ars.th_ierarhia_nodului")}</th>
          </tr>
        </thead>
        <tbody>
          {dirs.map(([d, info]) =>
            !info ? null : (
              <tr key={d}>
                <td><strong>{d}</strong>{info.caz ? ` (${info.caz})` : ""}</td>
                <td>
                  {info.nr_pereti ?? "—"} / {info.nr_stalpi ?? info.nr_elemente ?? "—"}
                  {info.nr_fasii_perete ? t("struct.ars.p1_fasii", { p1: info.nr_fasii_perete }) : ""}
                </td>
                {masurat ? (
                  <>
                    <td>{fmt(info.V_pereti_kn, 0)}</td>
                    <td>{fmt(info.V_restul_kn, 0)}</td>
                    <td>{fmt(info.V_baza_kn, 0)}</td>
                    <td>{fmt(info.neaditiv_kn, 0)} kN</td>
                  </>
                ) : (
                  <>
                    <td>{fmt(info.I_pereti_m4, 4)}</td>
                    <td>{fmt(info.I_total_m4, 4)}</td>
                  </>
                )}
                <td><strong>{fmt(100 * (info.fractie_pereti ?? 0), 1)} %</strong></td>
                <td>{fmt(100 * (info.prag ?? 0.5), 0)} %</td>
                <td>{t(info.sistem === "pereti"
                  ? "struct.ars.pereti_predominanti" : "struct.ars.cadre")}</td>
                <td>
                  {t(info.sistem === "pereti"
                    ? "struct.ars.nu_se_cere_informativ"
                    : "struct.ars.se_cere_fiecare_nod")}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
      {(s.X ?? s.Y)?.elemente_pereti?.length ? (
        <div className="sg-muted">
          {t("struct.ars.elementele_ca_pereti", {
            lista: [...new Set([...(s.X?.elemente_pereti ?? []),
                                ...(s.Y?.elemente_pereti ?? [])])].join(", "),
          })}
        </div>
      ) : null}
      <div className="sg-muted">
        {t("struct.ars.sursa_fractiunii", { sursa: (s.X ?? s.Y)?.sursa ?? "—" })}
      </div>
    </div>
  );
}
